import styled from 'styled-components';

export const PageWrapper = styled.div`
  background: #F3ECE1;
  min-height: 100vh;
  padding-top: 88px;
`;

const Wrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
`;

export const PageHead = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, .65fr);
  gap: 72px;
  align-items: end;
  padding-block: 88px 72px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  @media (max-width: 768px) {
    padding-block: 60px 48px;
    gap: 24px;
  }
`;

export const PageHeadContent = styled.div``;

export const Eyebrow = styled.span`
  display: block;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #D9442B;
`;

export const PageTitle = styled.h1`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(56px, 6.7vw, 96px);
  font-weight: 400;
  line-height: 0.93;
  margin: 18px 0 0;
  letter-spacing: -0.02em;
  text-wrap: balance;
  color: #1A1814;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 48px;
    line-height: 1;
    margin: 12px 0 0;
  }
`;

export const PageDescription = styled.p`
  margin: 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 21px;
  color: #4A453D;
  max-width: 26em;
  line-height: 1.55;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 1.6;
  }
`;

export const ServiceIndex = styled(Wrap)`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid #1A1814;
  border-bottom: 1px solid #D2C8B8;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const IndexLink = styled.a`
  display: block;
  padding: 20px 0 22px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  & + & {
    padding-left: 28px;
    border-left: 1px solid #D2C8B8;

    @media (max-width: 760px) {
      &:nth-child(3) {
        padding-left: 0;
        border-left: 0;
      }
    }
  }

  @media (max-width: 760px) {
    padding: 24px 16px;

    &:nth-child(-n+2) {
      border-bottom: 1px solid #D2C8B8;
    }

    & + & {
      padding-left: 16px;
    }
  }

  &:hover ${() => IndexTitle} {
    color: #D9442B;
  }

  &:focus-visible {
    outline: 2px solid #D9442B;
    outline-offset: 3px;
  }
`;

export const IndexLabel = styled.span`
  display: block;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #D9442B;
`;

export const IndexTitle = styled.b`
  display: block;
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 30px;
  font-weight: 400;
  margin-top: 6px;
  transition: color 0.2s ease;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

export const IndexSub = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 15px;
  color: #6B645A;
`;

export const ServiceGroup = styled.section`
  border-bottom: 1px solid #D2C8B8;
  scroll-margin-top: 24px;

  &.dark {
    background: #1A1814;
    color: #F3ECE1;
    border-bottom: 0;

    ${() => GroupTitle}, ${() => ServiceTitle} {
      color: #F3ECE1;
    }

    ${() => GroupIntroText}, ${() => ServiceDescription} {
      color: #BDB3A3;
    }

    ${() => ServiceList} {
      border-top-color: #F3ECE1;
    }

    ${() => ServiceItem} {
      border-bottom-color: #3A342C;
    }

    ${() => GroupCount} {
      color: #A89E8F;
      border-color: #3A342C;
    }
  }
`;

export const GroupInner = styled(Wrap)`
  display: grid;
  grid-template-columns: 420px minmax(0, 1fr);
  gap: 72px;
  padding-block: 88px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-block: 64px;
  }

  @media (max-width: 768px) {
    padding-block: 48px;
    gap: 32px;
  }
`;

export const GroupIntro = styled.div``;

export const GroupNum = styled.span`
  display: block;
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 150px;
  font-style: italic;
  font-weight: 400;
  line-height: 0.8;
  color: #D9442B;

  @media (max-width: 1100px) {
    font-size: 110px;
  }

  @media (max-width: 768px) {
    font-size: 80px;
  }
`;

export const GroupTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 64px;
  font-weight: 400;
  line-height: 1;
  margin: 24px 0 0;
  letter-spacing: -0.02em;
  color: #1A1814;

  @media (max-width: 768px) {
    font-size: 42px;
    margin: 16px 0 0;
  }
`;

export const GroupIntroText = styled.p`
  margin: 16px 0 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 19px;
  color: #4A453D;
  max-width: 18em;
  line-height: 1.55;

  @media (max-width: 768px) {
    font-size: 17px;
    margin: 12px 0 0;
  }
`;

export const GroupCount = styled.span`
  display: inline-block;
  margin-top: 24px;
  padding: 6px 10px;
  border: 1px solid #D2C8B8;
  color: #6B645A;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const ServiceList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid #1A1814;
`;

export const ServiceItem = styled.li`
  display: grid;
  grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr) 30px;
  gap: 32px;
  align-items: baseline;
  padding-block: 30px;
  border-bottom: 1px solid #D2C8B8;

  &:hover ${() => Arrow} {
    transform: translateX(6px);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding-block: 28px;
  }
`;

export const ServiceTitle = styled.h3`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 34px;
  font-weight: 400;
  line-height: 1.05;
  margin: 0;
  letter-spacing: -0.02em;
  color: #1A1814;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 1.1;
  }
`;

export const ServiceDescription = styled.p`
  margin: 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 17px;
  color: #4A453D;
  line-height: 1.55;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.6;
  }
`;

export const Arrow = styled.span`
  color: #D9442B;
  font-size: 22px;
  transition: transform 0.2s ease;

  @media (max-width: 760px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StartSection = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 72px;
  padding-block: 72px;
  border-bottom: 1px solid #D2C8B8;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding-block: 56px;
  }
`;

export const StartContent = styled.div``;

export const StartTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 56px;
  font-weight: 400;
  line-height: 1;
  margin: 16px 0 0;
  letter-spacing: -0.02em;
  text-wrap: balance;
  color: #1A1814;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 38px;
    margin: 12px 0 0;
  }
`;

export const StartList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid #1A1814;
`;

export const StartListItem = styled.li`
  display: grid;
  grid-template-columns: 60px 1fr;
  padding-block: 18px;
  border-bottom: 1px solid #D2C8B8;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  line-height: 1.55;

  @media (max-width: 768px) {
    grid-template-columns: 50px 1fr;
    font-size: 16px;
    padding-block: 16px;
  }
`;

export const StartNum = styled.span`
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 400;
  color: #D9442B;
  padding-top: 4px;
`;

export const ClosingSection = styled.section`
  background: #1A1814;
  color: #F3ECE1;
`;

export const ClosingWrap = styled(Wrap)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 40px;
  padding-block: 100px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding-block: 64px;
    gap: 32px;
  }
`;

export const ClosingTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(48px, 5.8vw, 84px);
  font-weight: 400;
  line-height: 0.95;
  max-width: 11em;
  margin: 0;
  letter-spacing: -0.02em;
  text-wrap: balance;
  color: #F3ECE1;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 42px;
    line-height: 1;
  }
`;

export const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 17px 26px;
  background: #F3ECE1;
  color: #1A1814;
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  transition: gap 0.2s ease;

  &:hover {
    gap: 18px;
  }

  &:focus-visible {
    outline: 2px solid #D9442B;
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 18px 24px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CTAArrow = styled.span`
  color: #D9442B;
`;
